import * as React from 'react';
import { connect } from 'react-redux';
import { ISearchCatalogItemSchema } from '@cian/api-models/monolith-python/cian-api/site/v1/search-regions-cities';
import { debounce } from '@cian/newbuilding-utils';

import { IApplicationState, TRegionSearchCitiesState } from '../../../../types/redux';
import { IRegion, IRegionsByID } from '../../../../types/regions';

import { CityModalInput } from './CityModalInput';
import { CityModalContent } from './CityModalContent';
import { Container } from '../../../../components/Container';
import {
  SIZE_16, SIZE_20, SIZE_24, SIZE_48, SIZE_WIDE,
} from '../../../../constants/sizes';
import { TTypedThunkDispatch } from '../../../../types/thunk';
import { getOffersCount, getOffersData } from '../../../../actions/offersData';
import { setCurrentRegion } from '../../../../actions/region/currentRegion';
import { getRegionSearchCities } from '../../../../actions/regionSearchCities';
import { getRegionCities, clearRegionCities } from '../../../../actions/regionCities';
import { DEFAULT_INPUT_TIMEOUT } from '../../../../constants/timeouts';
import { getRegionsByAlphabet } from '../../../../selectors/getRegionsByAlphabet';
import { getCitiesByAlphabet } from '../../../../selectors/getCitiesByAlphabet';
import { getRegionsColumns } from '../../../../selectors/getRegionsColumns';
import { isSPBAndLo, isMoscowAndMoscowArea, LO_ID, MOSCOW_AREA_ID } from '../../../../utils/geo';
import { CityModalFooter } from './CityModalFooter';
import { geoDescriptor, regionDescriptor } from '../../../../constants/descriptors';
import { createSimpleAction, createAction } from '../../../сonnectToDescriptors';
import { clearTagsAfterChangeRegion, updateTags } from '../../../../actions/filterTags';
import { trackingActionPopupGeoSave } from '../../../../actions/analytics';

export type TCityModalStep = 1 | 2;

interface IModalOwnProps {
  onSubmitModal(): void;
  onToggleSuggest(isOpenSuggest: boolean): void;
}

interface IModalStoreProps {
  count: number | null;
  isCountFetching?: boolean;
  searchList: TRegionSearchCitiesState;
  regionsByID: IRegionsByID;
  regionColumns: IRegion[][][];
  citiesColumns: IRegion[][][];
  mainRegions?: (IRegion | ISearchCatalogItemSchema)[];
  mainCities?: (IRegion | ISearchCatalogItemSchema)[];
  currentRegion: IRegion | ISearchCatalogItemSchema;
  canUseHiddenBase: boolean;
}

interface IModalState {
  currentRegion: IRegion | ISearchCatalogItemSchema;
  step: TCityModalStep;
}

interface IModalDispatchProps {
  onSubmit(canUseHiddenBase: boolean): void;
  onInput(text: string): void;
  onRegionChange(regionId: number): void;
  clearCities(): void;
  setRegion(region: IRegion | ISearchCatalogItemSchema, previousRegion: IRegion | ISearchCatalogItemSchema): void;
  setRegionFilter(id: number): void;
  getOffersCount(region?: IRegion | ISearchCatalogItemSchema): void;
}

type TModalProps = IModalOwnProps & IModalStoreProps & IModalDispatchProps;

class CityModalComponent extends React.PureComponent<TModalProps, IModalState> {
  public constructor(props: TModalProps) {
    super(props);

    this.state = {
      step: 1,
      currentRegion: props.currentRegion,
    };
  }
  // tslint:disable-next-line:no-any
  public sTimeout: any;

  public componentWillUnmount() {
    clearTimeout(this.sTimeout);
  }

  public render() {
    const {
      count,
      isCountFetching,
      searchList = [],
      mainRegions = [],
      mainCities = [],
      regionsByID = {},
      regionColumns = [[], [], [], []],
      citiesColumns = [[], [], [], []],
    } = this.props;
    const {
      currentRegion,
      step,
    } = this.state;

    return (
      <>
        <Container
          padding={`${SIZE_16} ${SIZE_20}`}
          boxShadow="0 5px 10px 0 rgba(0, 0, 0, 0.1)"
          shadow
        >
           <CityModalInput
             onClick={this.handleListClick}
             onCloseSuggest={this.handleCloseSuggest}
             onChange={this.handleInputChange}
             searchList={searchList}
           />
        </Container>
        <Container
          padding={`${SIZE_24} ${SIZE_20} 0`}
          margin={`0 0 ${SIZE_48}`}
          height={SIZE_WIDE}
          overflow="auto"
        >
          <CityModalContent
            step={step}
            currentRegion={currentRegion}
            regionsByID={regionsByID}
            mainRegions={step === 1 ? mainRegions : mainCities}
            regionColumns={
              step === 2 && (citiesColumns[0].length || mainRegions.length)
                ? citiesColumns
                : regionColumns
            }
            onClick={this.handleRegionClick}
          />
        </Container>
        <CityModalFooter
          count={count}
          isCountFetching={isCountFetching}
          step={step}
          onBackClick={this.handleBack}
          onSubmit={this.handleSubmit}
        />
      </>
    );
  }

  private setDeliverState = (newState: IModalState) => {
    this.sTimeout = setTimeout(() => this.setState(newState), 0);
  }

  private deliverFn = (fn: () => void) => {
    this.sTimeout = setTimeout(fn, 0);
  }

  private handleInputChange = debounce(DEFAULT_INPUT_TIMEOUT, (inputValue: string) => {
    this.props.onToggleSuggest(true);
    this.props.onInput(inputValue);

  });

  private handleCloseSuggest = () => {
    this.props.onToggleSuggest(false);
  }

  private handleSubmit = () => {
    const {
      setRegion, onSubmit, onSubmitModal, setRegionFilter, canUseHiddenBase, currentRegion: previousRegion,
    } = this.props;
    const { currentRegion } = this.state;

    if (currentRegion !== this.props.currentRegion) {
      setRegionFilter(currentRegion.id);
      setRegion(currentRegion, previousRegion);
      onSubmit(canUseHiddenBase);
    }

    onSubmitModal();
  }

  private handleBack = () => {
    this.setDeliverState({ step: 1, currentRegion: this.props.currentRegion });
    this.props.clearCities();
    this.props.getOffersCount();
  }

  private handleRegionClick = (region: IRegion): void => {
    let capitalID;

    if (isMoscowAndMoscowArea(region)) {
      capitalID = MOSCOW_AREA_ID;
    } else if (isSPBAndLo(region)) {
      capitalID = LO_ID;
    }

    this.changeRegion(region, 2, capitalID);
  }

  private handleListClick = (options: { id: string, label: string }) => {
    const region = this.props.searchList[parseInt(options.id, 10)];
    this.changeRegion(region, 2, region.parentId, true);
    this.deliverFn(() => this.props.onToggleSuggest(false));
  }

  private changeRegion = (
    region: IRegion | ISearchCatalogItemSchema,
    targetStep: TCityModalStep,
    parentId?: number,
    fromSearch?: boolean,
  ) => {
    const { onRegionChange } = this.props;
    const { currentRegion } = this.state;

    if (this.state.step === 1 || fromSearch) {
      onRegionChange(parentId || region.id);
    }

    this.setDeliverState({
      currentRegion: region,
      step: targetStep,
    });

    if (region.id !== currentRegion.id) {
      this.props.getOffersCount(region);
    }
  }
}

function mapStateToProps(state: IApplicationState): IModalStoreProps {
  const {
    temp: { regionSearchCities, regionCities },
    region: { currentRegion, regionsByID },
  } = state;
  const regions = getRegionsByAlphabet(state);
  const regionsLength = state.region.regions.length - regions._.length;
  const regionColumns = getRegionsColumns(regions, regionsLength);
  const cities = getCitiesByAlphabet(state);
  const citiesLength = (regionCities || []).length - cities._.length;
  const citiesColumns = getRegionsColumns(cities, citiesLength);
  const count = state.filters.offersCount.count;
  const isCountFetching = state.filters.offersCount.isFetching;

  return {
    count,
    isCountFetching,
    regionsByID,
    currentRegion,
    mainRegions: regions._ as IRegion[],
    mainCities: cities._ as IRegion[],
    regionColumns,
    citiesColumns,
    searchList: regionSearchCities && regionSearchCities.length
      ? regionSearchCities.slice(0, 4)
      : [],
    canUseHiddenBase: state.user.isAuthenticated && state.user.permissions.canUseHiddenBase,
  };
}

function mapDispatchToProps(dispatch: TTypedThunkDispatch) {
  return {
    onSubmit: (canUseHiddenBase: boolean) => {
      dispatch(getOffersData({
        trackingAction: trackingActionPopupGeoSave,
        canUseHiddenBase,
      }));
    },
    onInput: (text: string) => dispatch(getRegionSearchCities(text)),
    onRegionChange: (regionId: number) => {
      dispatch(getRegionCities(regionId)).catch(() => {});
    },
    clearCities: () => dispatch(clearRegionCities()),
    setRegion: (region: IRegion, previousRegion: IRegion) => {
      dispatch(setCurrentRegion(region));
      dispatch(clearTagsAfterChangeRegion(previousRegion.id));
      dispatch(updateTags());
      createSimpleAction(dispatch, geoDescriptor[1])();
    },
    setRegionFilter: createAction(dispatch, regionDescriptor[0]),
    getOffersCount: (region?: IRegion) => {
      dispatch(getOffersCount(region));
    },
  };
}

export const CityModal = connect(mapStateToProps, mapDispatchToProps)(CityModalComponent);
